import { useEffect, useState, type ComponentType, type ReactNode } from 'react';
import { assets } from '../assets';
import { useConsoleI18n } from './bind';
import { useMediaQuery } from './media';
import { findTrail, findTrailById, isBranch, isOnPath, nodeId, type NavNode } from './navTree';

/** Below this width the sidebar becomes a top bar with a drawer. */
export const DRAWER_QUERY = '(max-width: 1024px)';

/** A sidebar group with its links, labels already translated (the flat shape; `groupsToNav` turns it into a tree). */
export interface ShellNavGroup {
  id: string;
  label: string;
  items: { path: string; label: string }[];
}

/**
 * Flat groups as a menu tree: a group with one link labelled like the group is just that link (a heading link), the
 * others are folding groups without a page of their own.
 */
export const groupsToNav = (groups: ShellNavGroup[]): NavNode[] =>
  groups
    .filter((g) => g.items.length)
    .map((g) =>
      g.items.length === 1 && g.items[0].label === g.label
        ? { id: g.id, label: g.label, path: g.items[0].path }
        : { id: g.id, label: g.label, children: g.items.map((n) => ({ path: n.path, label: n.label })) }
    );

/**
 * The router's NavLink (react-router-dom), so the shell needs no router of its own. With `activeId` the shell lights
 * the node itself: pass the router's plain `Link` then, or a NavLink lights every node on the path as well.
 */
export type ShellLink = ComponentType<{
  to: string;
  end?: boolean;
  className?: string;
  onClick?: () => void;
  'aria-current'?: 'page';
  children?: ReactNode;
}>;

/** The NYCE.LOGIC mark and word, with an optional caption under it (the portal's "Client portal"). */
export function ConsoleLogo({ caption }: { caption?: ReactNode }) {
  return (
    <span className="nyk-brand">
      <img src={assets.markIconWhite} alt="" className="nyk-brand-mark" />
      <span className="nyk-brand-text">
        NYCE<span>.LOGIC</span>
        {caption && <small>{caption}</small>}
      </span>
    </span>
  );
}

interface NavProps {
  /** The menu the caller may see (`visibleTree`), in screen order. */
  nav: NavNode[];
  home: string;
  pathname: string;
  /**
   * The lit node, by id (`nodeId`), when the path cannot tell (views that differ in the query only). Without it the
   * open branch follows the path and the Link lights itself.
   */
  activeId?: string;
  Link: ShellLink;
  onNavigate?: () => void;
}

/** The trail to the open page: by `activeId` when the app names it, else by the path. */
const trailOf = (nav: NavNode[], home: string, pathname: string, activeId?: string) =>
  activeId !== undefined ? findTrailById(nav, activeId) : findTrail(nav, pathname === '/' ? home : pathname);

const listId = (node: NavNode) => `nyk-nav-${nodeId(node).replace(/[^a-z0-9]+/gi, '-')}`;

/** A node's label, after its icon when it has one. */
const labelOf = (node: NavNode) =>
  node.icon ? (
    <>
      <span className="nyk-nav-icon" aria-hidden="true">
        {node.icon}
      </span>
      {node.label}
    </>
  ) : (
    node.label
  );

/**
 * The menu tree; used by the sidebar and by the mobile drawer. An accordion on every level: every branch starts folded
 * except those holding the open page, and opening a branch folds its siblings (and everything under them). A
 * top-level page is a heading link; a branch with a `path` opens its page from its name, the chevron folds it.
 */
export function NavList({ nav, home, pathname, activeId, Link, onNavigate }: NavProps) {
  const { t } = useConsoleI18n();
  const trail = trailOf(nav, home, pathname, activeId);
  const here = trail[trail.length - 1];
  // A page whose path holds a sibling's (`/databases` and `/databases/index-advisor`) stays dark on that sibling's
  // page: its link matches exactly there; on its own detail pages it still lights by prefix.
  const endOf = (node: NavNode, end: boolean | undefined) =>
    end ??
    (here && here !== node && node.path && here.path && isOnPath(node.path, here.path) ? true : undefined);
  const branches = trail.filter(isBranch).map(nodeId);
  const active = branches.length ? branches : trail.slice(0, 1).map(nodeId);
  // The open branches, root first: one per level.
  const [open, setOpen] = useState<string[]>(active);
  // Only a move to another branch refolds the menu; paging within the open one leaves the user's choice.
  const activeKey = active.join('\n');
  useEffect(() => {
    if (activeKey) setOpen(activeKey.split('\n'));
  }, [activeKey]);

  // With activeId the shell lights the node; otherwise the Link does.
  const lit = (id: string, className: string) =>
    activeId !== undefined && id === activeId
      ? { className: `${className} active`, 'aria-current': 'page' as const }
      : { className };
  const leaf = (node: NavNode, id: string, className: string, end: boolean | undefined) =>
    node.path ? (
      <Link key={id} to={node.path} end={endOf(node, end)} onClick={onNavigate} {...lit(id, className)}>
        {labelOf(node)}
      </Link>
    ) : (
      <button
        key={id}
        type="button"
        className={lit(id, className).className}
        onClick={() => {
          node.onSelect?.();
          onNavigate?.();
        }}
      >
        {labelOf(node)}
      </button>
    );

  const items = (nodes: NavNode[], ancestors: string[]): ReactNode =>
    nodes.map((node) => {
      const id = nodeId(node);
      const depth = ancestors.length;
      if (!isBranch(node))
        return depth === 0 ? (
          <div key={id} className="nyk-nav-group" data-nav-group={node.label} data-nav-node={id}>
            <div className="nyk-nav-heading nyk-nav-heading-split">
              {leaf(node, id, 'nyk-nav-heading-link', node.end ?? true)}
            </div>
          </div>
        ) : (
          leaf(node, id, 'nyk-nav-link', node.end)
        );
      const isOpen = open[depth] === id;
      const fold = {
        'aria-expanded': isOpen,
        'aria-controls': listId(node),
        onClick: () => setOpen(isOpen ? ancestors : [...ancestors, id]),
      };
      const chevron = <span className="nyk-nav-chevron" aria-hidden="true" />;
      const headingClass = depth === 0 ? 'nyk-nav-heading' : 'nyk-nav-sub-heading';
      const head = node.path ? (
        <div className={`${headingClass} nyk-nav-heading-split`}>
          <Link
            to={node.path}
            end={node.end ?? true}
            {...lit(id, 'nyk-nav-heading-link')}
            onClick={() => {
              setOpen([...ancestors, id]);
              onNavigate?.();
            }}
          >
            {labelOf(node)}
          </Link>
          <button
            type="button"
            className="nyk-nav-fold"
            aria-label={t(isOpen ? 'shell.collapse' : 'shell.expand', { label: node.label })}
            {...fold}
          >
            {chevron}
          </button>
        </div>
      ) : (
        <button type="button" className={headingClass} {...fold}>
          {labelOf(node)}
          {chevron}
        </button>
      );
      return (
        <div
          key={id}
          className={depth === 0 ? 'nyk-nav-group' : 'nyk-nav-sub'}
          data-nav-group={depth === 0 ? node.label : undefined}
          data-nav-node={id}
          data-collapsed={isOpen ? undefined : ''}
        >
          {head}
          <div id={listId(node)} className="nyk-nav-items">
            {isOpen && items(node.children!, [...ancestors, id])}
          </div>
        </div>
      );
    });

  return <div className="nyk-nav-groups">{items(nav, [])}</div>;
}

/** Slide-in navigation for narrow screens: closes on navigation, Escape and the backdrop. */
function NavDrawer({
  open,
  onClose,
  logo,
  header,
  footer,
  nav,
}: {
  open: boolean;
  onClose: () => void;
  logo: ReactNode;
  header: ReactNode;
  footer: ReactNode;
  nav: NavProps;
}) {
  const { t } = useConsoleI18n();
  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav.pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="nyk-drawer-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside
        className="nyk-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={t('shell.navigation')}
        data-testid="nav-drawer"
      >
        <div className="nyk-drawer-head">
          {logo}
          <button type="button" className="nyk-menu-btn" aria-label={t('shell.closeMenu')} onClick={onClose}>
            <span className="nyk-menu-x" aria-hidden="true">
              ×
            </span>
          </button>
        </div>
        {header}
        <NavList {...nav} onNavigate={onClose} />
        {footer}
      </aside>
    </div>
  );
}

/**
 * The console layout: the sidebar (logo, menu tree, user box) that becomes a top bar with a drawer on narrow screens,
 * and the main area. With `documentTitle` the document title follows the open page (`<page> | <documentTitle>`).
 */
export function ConsoleShell({
  logo,
  nav,
  home,
  pathname,
  activeId,
  Link,
  navHeader,
  userBox,
  topbarEnd,
  overlays,
  documentTitle,
  children,
}: {
  logo: ReactNode;
  /** The menu the caller may see (`visibleTree`); flat groups convert with `groupsToNav`. */
  nav: NavNode[];
  /** Where `/` goes (its branch is the open one there). */
  home: string;
  pathname: string;
  /** The lit node by id, when the path cannot tell (see `NavList`); pass the router's plain `Link` with it. */
  activeId?: string;
  Link: ShellLink;
  /** Above the menu in the sidebar and the drawer (a current-project button); `closeMenu` closes the drawer. */
  navHeader?: (closeMenu: () => void) => ReactNode;
  /** The sidebar/drawer user box; `closeMenu` closes the drawer (e.g. before opening another panel). */
  userBox: (closeMenu: () => void) => ReactNode;
  /** Right end of the narrow-screen top bar (a user name, a scope picker). */
  topbarEnd?: ReactNode;
  /** Drawers and dialogs of the shell (an activity feed). */
  overlays?: ReactNode;
  /** The product name in the document title; the title is left alone without it. */
  documentTitle?: string;
  children: ReactNode;
}) {
  const { t } = useConsoleI18n();
  const compact = useMediaQuery(DRAWER_QUERY);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    if (!compact) setMenuOpen(false);
  }, [compact]);
  const title = trailOf(nav, home, pathname, activeId).at(-1)?.label;
  useEffect(() => {
    if (documentTitle) document.title = title ? `${title} | ${documentTitle}` : documentTitle;
  }, [title, documentTitle]);
  const closeMenu = () => setMenuOpen(false);
  const navProps: NavProps = { nav, home, pathname, activeId, Link };
  const header = navHeader && <div className="nyk-nav-header">{navHeader(closeMenu)}</div>;
  return (
    <div className={`nyk-layout${compact ? ' nyk-layout-compact' : ''}`}>
      {compact ? (
        <header className="nyk-topbar">
          <button
            type="button"
            className="nyk-menu-btn"
            aria-label={t('shell.openMenu')}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
          >
            <span className="nyk-menu-icon" aria-hidden="true" />
          </button>
          {logo}
          {topbarEnd && <span className="nyk-topbar-user">{topbarEnd}</span>}
        </header>
      ) : (
        <nav className="nyk-nav" aria-label={t('shell.navigation')}>
          {logo}
          {header}
          <NavList {...navProps} />
          {userBox(closeMenu)}
        </nav>
      )}
      {compact && (
        <NavDrawer
          open={menuOpen}
          onClose={closeMenu}
          logo={logo}
          header={header}
          footer={userBox(closeMenu)}
          nav={navProps}
        />
      )}
      {overlays}
      <main className="nyk-main">{children}</main>
    </div>
  );
}
